"use client";

import { useRef, useState, useEffect } from "react";
import { AnimatePresence, m } from "motion/react";
import { Button } from "@/components/button";
import { duration, easing } from "@/lib/animation/tokens";
import Script from "next/script";
import messages from "@/messages/en.json";
import type { ContactFormProps } from "./contact-form.interface";

/**
 * Small inline spinner shown in the submit button while sending.
 *
 * A constant linear rotation needs no React/Motion integration — it's a
 * pure CSS keyframe (`.spinner-rotate` in globals.css, which also stops
 * under `prefers-reduced-motion`). Keeping it out of Motion avoids
 * mounting an animated component for a decorative spin.
 */
function Spinner() {
  return (
    <svg
      className="spinner-rotate"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  );
}

const errorVariants = {
  hidden: { opacity: 0, height: 0, y: -4 },
  visible: {
    opacity: 1,
    height: "auto",
    y: 0,
    transition: { duration: duration.sm, ease: easing.out },
  },
  exit: {
    opacity: 0,
    height: 0,
    y: -4,
    transition: { duration: duration.xs, ease: easing.out },
  },
};

const statusVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: duration.sm, ease: easing.out },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: duration.xs, ease: easing.out },
  },
};

const t = messages.visitUs.form;
const v = t.validation;

type FormStatus = "idle" | "sending" | "success" | "error";
type TouchedFields = { name: boolean; email: boolean; message: boolean };

interface TurnstileWindow extends Window {
  turnstile?: {
    render: (
      container: HTMLElement,
      options: {
        sitekey: string;
        callback: (token: string) => void;
        "expired-callback": () => void;
        "error-callback": () => void;
      },
    ) => string;
    reset: (widgetId: string) => void;
  };
}

function validateName(val: string) {
  return val.trim() ? "" : v.nameRequired;
}

function validateEmail(val: string) {
  if (!val.trim()) return v.emailRequired;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()) ? "" : v.emailInvalid;
}

function validateMessage(val: string) {
  return val.trim() ? "" : v.messageRequired;
}

export function ContactForm({ heading, description }: ContactFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [token, setToken] = useState("");
  const [touched, setTouched] = useState<TouchedFields>({ name: false, email: false, message: false });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [status, setStatus] = useState<FormStatus>("idle");

  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const errors = {
    name: validateName(name),
    email: validateEmail(email),
    message: validateMessage(message),
  };
  const isValid = !errors.name && !errors.email && !errors.message;

  function renderWidget() {
    const w = window as TurnstileWindow;
    if (!w.turnstile || !containerRef.current || widgetIdRef.current !== null) return;
    widgetIdRef.current = w.turnstile.render(containerRef.current, {
      sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "",
      callback: (t) => setToken(t),
      "expired-callback": () => setToken(""),
      "error-callback": () => setToken(""),
    });
  }

  // Render if script was already cached when component mounts
  useEffect(() => { renderWidget(); }, []);

  function touch(field: keyof TouchedFields) {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    setSubmitAttempted(true);

    if (!isValid) return;
    if (!token) return;
    if (honeypot) return;

    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, website: honeypot, turnstileToken: token }),
      });

      if (res.ok) {
        setStatus("success");
        setName("");
        setEmail("");
        setMessage("");
        setToken("");
        setTouched({ name: false, email: false, message: false });
        setSubmitAttempted(false);
        if (widgetIdRef.current !== null) {
          (window as TurnstileWindow).turnstile?.reset(widgetIdRef.current);
        }
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  function fieldClass(field: keyof TouchedFields) {
    const invalid = touched[field] && !!errors[field];
    return [
      "w-full rounded-button border bg-white px-3 py-2 text-sm text-hop focus:outline-none focus:ring-2 transition-colors duration-200",
      invalid ? "border-red-400 focus:ring-red-400" : "border-hop focus:border-copper-kettle focus:ring-copper-kettle",
    ].join(" ");
  }

  return (
    <div className="rounded-card bg-white py-6 px-4.5">
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        strategy="afterInteractive"
        onLoad={renderWidget}
      />

      <h2 className="text-hop mb-6 text-[22px] lg:text-[24px] font-bold">{heading}</h2>
      <p className="text-hop mb-9 text-body font-light">{description}</p>

      {/* Honeypot — positioned off-screen so real users never see it */}
      <div
        style={{ position: "absolute", left: "-9999px", top: "auto" }}
        aria-hidden="true"
      >
        <label htmlFor="hp-website">Website</label>
        <input
          id="hp-website"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {status === "success" ? (
          <m.p
            key="success"
            role="status"
            variants={statusVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="text-copper-kettle text-sm font-medium"
          >
            {t.successMessage}
          </m.p>
        ) : (
          <m.form
            key="form"
            onSubmit={handleSubmit}
            noValidate
            className="space-y-4"
            variants={statusVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div>
              <label htmlFor="cf-name" className="text-hop mb-2 block text-body font-medium">
                {t.nameLabel}
              </label>
              <input
                id="cf-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => touch("name")}
                autoComplete="name"
                aria-describedby={touched.name && errors.name ? "cf-name-error" : undefined}
                aria-invalid={touched.name && !!errors.name}
                className={`${fieldClass("name")} h-12.5`}
              />
              <AnimatePresence initial={false}>
                {touched.name && errors.name && (
                  <m.p
                    key="name-error"
                    id="cf-name-error"
                    role="alert"
                    variants={errorVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="overflow-hidden text-xs text-red-500"
                  >
                    <span className="mt-1 block">{errors.name}</span>
                  </m.p>
                )}
              </AnimatePresence>
            </div>

            <div>
              <label htmlFor="cf-email" className="text-hop mb-2 block text-body font-medium">
                {t.emailLabel}
              </label>
              <input
                id="cf-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => touch("email")}
                autoComplete="email"
                aria-describedby={touched.email && errors.email ? "cf-email-error" : undefined}
                aria-invalid={touched.email && !!errors.email}
                className={`${fieldClass("email")} h-12.5`}
              />
              <AnimatePresence initial={false}>
                {touched.email && errors.email && (
                  <m.p
                    key="email-error"
                    id="cf-email-error"
                    role="alert"
                    variants={errorVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="overflow-hidden text-xs text-red-500"
                  >
                    <span className="mt-1 block">{errors.email}</span>
                  </m.p>
                )}
              </AnimatePresence>
            </div>

            <div>
              <label htmlFor="cf-message" className="text-hop mb-2 block text-body font-medium">
                {t.messageLabel}
              </label>
              <textarea
                id="cf-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onBlur={() => touch("message")}
                rows={6}
                aria-describedby={touched.message && errors.message ? "cf-message-error" : undefined}
                aria-invalid={touched.message && !!errors.message}
                className={fieldClass("message")}
              />
              <AnimatePresence initial={false}>
                {touched.message && errors.message && (
                  <m.p
                    key="message-error"
                    id="cf-message-error"
                    role="alert"
                    variants={errorVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="overflow-hidden text-xs text-red-500"
                  >
                    <span className="mt-1 block">{errors.message}</span>
                  </m.p>
                )}
              </AnimatePresence>
            </div>

            {/* Turnstile widget */}
            <div ref={containerRef} />
            <AnimatePresence initial={false}>
              {submitAttempted && !token && (
                <m.p
                  key="turnstile-error"
                  role="alert"
                  variants={errorVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="overflow-hidden text-xs text-red-500"
                >
                  {t.turnstileIncomplete}
                </m.p>
              )}
            </AnimatePresence>

            <p className="font-mono text-mono-xs lg:text-mono-sm font-normal text-hop mb-6">{t.privacyNotice}</p>

            <AnimatePresence initial={false}>
              {status === "error" && (
                <m.p
                  key="status-error"
                  role="alert"
                  variants={errorVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="overflow-hidden text-sm text-red-600"
                >
                  {t.errorMessage}
                </m.p>
              )}
            </AnimatePresence>

            <Button
              type="submit"
              disabled={status === "sending"}
              className="disabled:opacity-50"
            >
              <span className="inline-flex items-center gap-2">
                {status === "sending" && <Spinner />}
                {status === "sending" ? t.sendingLabel : t.submitLabel}
              </span>
            </Button>
          </m.form>
        )}
      </AnimatePresence>
    </div>
  );
}
